import { Texture } from 'pixi.js';

import type { GameAssets } from '../../game/assets/GameAssets';
import { terrainConfig, type TerrainRenderAsset } from '../../game/terrain/TerrainRenderConfig';
import type { SeasonState } from '../seasons/SeasonTypes';
import type { MapTile } from '../world/types';
import { seasonalTerrainKey } from './DynamicWorldRules';

interface TerrainResolverContext {
  readonly season?: SeasonState;
  readonly visualPolish?: boolean;
  readonly waterMode?: 'tiles';
  readonly isRoad: (x: number, y: number) => boolean;
  readonly isWater: (x: number, y: number) => boolean;
}

interface Neighbors {
  readonly n: boolean;
  readonly s: boolean;
  readonly e: boolean;
  readonly w: boolean;
}

export class TerrainTextureResolver {
  constructor(private readonly assets: GameAssets) {}

  ground(tile: MapTile, context: Pick<TerrainResolverContext, 'season' | 'visualPolish'>): Texture {
    if (context.visualPolish) return this.configuredTexture(terrainConfig.base.visualPolish, context.season);
    const variantAsset = tile.variant ? terrainConfig.base.variants[tile.variant] : undefined;
    return this.asset(variantAsset ?? terrainConfig.base.default);
  }

  overlay(tile: MapTile, context: TerrainResolverContext): Texture | undefined {
    if (tile.type === 'road') return this.road(tile, context);
    if (tile.type === 'water') return this.water(tile, context);
    if (tile.type === 'shore') {
      if (context.visualPolish) return this.configuredTexture(terrainConfig.water.visualPolish.lakeShore, context.season);
      return this.configuredTexture(terrainConfig.overlays.shore, context.season);
    }
    if (tile.type === 'field') return this.configuredTexture(terrainConfig.overlays.field, context.season);
    if (context.visualPolish && (tile.type === 'forest' || tile.type === 'mountain')) return undefined;
    if (tile.type === 'forest') return this.asset(terrainConfig.overlays.forest.assets[(tile.x + tile.y) % terrainConfig.overlays.forest.assets.length]);
    if (tile.type === 'mountain') return this.configuredTexture(terrainConfig.overlays.mountain, context.season);
    if (tile.type === 'workyard') return this.configuredTexture(terrainConfig.overlays.workyard, context.season);
    return undefined;
  }

  road(tile: MapTile, context: TerrainResolverContext): Texture {
    const neighbors = this.roadNeighbors(tile, context);
    const count = neighborCount(neighbors);
    const roads = terrainConfig.roads.visualPolish;

    if (count >= 4) return this.configuredTexture(roads.cross, context.season);
    if (count === 3) return this.configuredTexture(roads.t, context.season);
    if ((neighbors.n || neighbors.s) && (neighbors.e || neighbors.w)) return this.configuredTexture(roads.corner, context.season);
    if (count === 1) {
      if (neighbors.e || neighbors.w) return this.configuredTexture(roads.horizontal, context.season);
      return this.configuredTexture(roads.vertical, context.season);
    }
    if (neighbors.e && neighbors.w && !(neighbors.n || neighbors.s)) return this.configuredTexture(roads.horizontal, context.season);
    return this.configuredTexture(roads.vertical, context.season);
  }

  water(tile: MapTile, context: TerrainResolverContext): Texture {
    const neighbors = this.waterNeighbors(tile, context);
    const count = neighborCount(neighbors);

    if (context.visualPolish) {
      const water = terrainConfig.water.visualPolish;
      if (tile.variant?.startsWith('water27/river_')) return this.cleanedWater27(tile.variant);
      if (tile.variant?.startsWith('water27/sea_deep')) return this.cleanedWater27(tile.variant);
      if (tile.variant?.startsWith('water27/shallow')) return this.cleanedWater27(tile.variant);
      if (tile.variant?.startsWith('water27/shore_')) return this.cleanedWater27(tile.variant);
      if (tile.variant?.startsWith('water27/bridge_')) return this.cleanedWater27(tile.variant);
      if (tile.variant === 'bridge_h') return this.configuredTexture(water.riverHorizontal, context.season);
      if (tile.variant === 'bridge_v') return this.configuredTexture(water.riverVertical, context.season);
      if (tile.variant?.includes('river_cross')) return this.configuredTexture(water.riverCross, context.season);
      if (tile.variant?.includes('river_t_')) return this.configuredTexture(water.riverT, context.season);
      if (tile.variant?.includes('river_h')) return this.configuredTexture(water.riverHorizontal, context.season);
      if (tile.variant?.includes('river_v')) return this.configuredTexture(water.riverVertical, context.season);
      if (tile.variant?.includes('river_bend') || tile.variant?.startsWith('river_')) return this.configuredTexture(water.riverBend, context.season);
      if (tile.variant?.includes('shore_out') || tile.variant?.includes('shore_in')) return this.configuredTexture(water.lakeCorner, context.season);
      if (tile.variant?.includes('shore_')) return this.configuredTexture(water.lakeShore, context.season);
      return this.configuredTexture(water.lake, context.season);
    }

    const water = terrainConfig.water.default;
    if (count >= 4) return this.asset((tile.x + tile.y) % 2 === 0 ? water.deepA : water.deepB);
    if (neighbors.n && neighbors.s && !neighbors.e && !neighbors.w) return this.asset(water.riverVertical);
    if (neighbors.e && neighbors.w && !neighbors.n && !neighbors.s) return this.asset(water.riverHorizontal);
    if (!neighbors.n && !neighbors.w) return this.asset(water.shoreOuterNorthWest);
    if (!neighbors.n && !neighbors.e) return this.asset(water.shoreOuterNorthEast);
    if (!neighbors.s && !neighbors.w) return this.asset(water.shoreOuterSouthWest);
    if (!neighbors.s && !neighbors.e) return this.asset(water.shoreOuterSouthEast);
    if (!neighbors.n) return this.asset(water.shoreNorth);
    if (!neighbors.s) return this.asset(water.shoreSouth);
    if (!neighbors.e) return this.asset(water.shoreEast);
    if (!neighbors.w) return this.asset(water.shoreWest);
    return this.asset(water.shallow);
  }

  private roadNeighbors(tile: MapTile, context: TerrainResolverContext): Neighbors {
    return {
      n: context.isRoad(tile.x, tile.y - 1),
      s: context.isRoad(tile.x, tile.y + 1),
      e: context.isRoad(tile.x + 1, tile.y),
      w: context.isRoad(tile.x - 1, tile.y),
    };
  }

  private waterNeighbors(tile: MapTile, context: TerrainResolverContext): Neighbors {
    return {
      n: context.isWater(tile.x, tile.y - 1),
      s: context.isWater(tile.x, tile.y + 1),
      e: context.isWater(tile.x + 1, tile.y),
      w: context.isWater(tile.x - 1, tile.y),
    };
  }

  private configuredTexture(asset: TerrainRenderAsset, season?: SeasonState): Texture {
    if (season && asset.seasonal) {
      return this.assets.reference[seasonalTerrainKey(asset.seasonal, season.currentSeason)] ?? this.asset(asset.asset);
    }
    return this.asset(asset.asset);
  }

  private asset(key: string): Texture {
    return this.assets.reference[key] ?? Texture.EMPTY;
  }

  private cleanedWater27(key: string): Texture {
    const cleanKey = `sk/${key.replace('water27/', 'water27_')}_clean`;
    return this.assets.reference[cleanKey] ?? this.asset(key);
  }
}

function neighborCount(neighbors: Neighbors): number {
  return Number(neighbors.n) + Number(neighbors.s) + Number(neighbors.e) + Number(neighbors.w);
}
